var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { pxToRem } from "../ThemeProvider/typography";
import { LinkAdapter, } from "../LinkAdapter/LinkAdapter";
const styleProps = {
    variant: true,
    size: true,
    edge: true,
    startIcon: true,
    endIcon: true,
    responsive: true,
    color: true,
};
const shouldForwardButtonProp = (prop) => !styleProps[prop];
const DEFAULT_PROPS = {
    variant: "primary",
    size: "medium",
    edge: "rounded",
    responsive: false,
};
const BORDER_WIDTHS = {
    small: 1,
    medium: 1,
    large: 2,
};
const RESPONSIVE_SIZES = {
    small: "small",
    medium: "small",
    large: "medium",
};
const sizeStyles = (size, hasBorder, theme) => {
    const paddingAdjust = hasBorder ? BORDER_WIDTHS[size] : 0;
    return [
        {
            boxSizing: "border-box",
            borderWidth: BORDER_WIDTHS[size],
        },
        size === "large" && Object.assign({ padding: `${14 - paddingAdjust}px 24px` }, theme.typography.buttonLarge),
        size === "medium" && Object.assign({ padding: `${11 - paddingAdjust}px 16px` }, theme.typography.button),
        size === "small" && Object.assign({ padding: `${8 - paddingAdjust}px 12px` }, theme.typography.buttonSmall),
    ];
};
const buttonStyles = (props) => {
    const { size, variant, edge, theme, color, responsive } = Object.assign(Object.assign({}, DEFAULT_PROPS), props);
    const { colors } = theme.custom;
    const hasBorder = variant === "secondary" || variant === "bordered";
    return css([
        {
            color: theme.palette.text.primary,
            textAlign: "center",
            // display
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            // transitions
            transition: `background ${theme.transitions.duration.short}ms`,
            // cursor
            cursor: "pointer",
            ":disabled": {
                cursor: "default",
            },
            minWidth: "100px",
        },
        ...sizeStyles(size, hasBorder, theme),
        // responsive
        responsive && {
            [theme.breakpoints.down("sm")]: sizeStyles(RESPONSIVE_SIZES[size], hasBorder, theme),
        },
        // variant
        variant === "primary" && {
            backgroundColor: colors.mitRed,
            color: colors.white,
            border: "none",
            /* Shadow/04dp */
            boxShadow: "0px 2px 4px 0px rgba(37, 38, 43, 0.10), 0px 3px 8px 0px rgba(37, 38, 43, 0.12)",
            ":hover:not(:disabled)": {
                backgroundColor: colors.red,
                boxShadow: "none",
            },
            ":disabled": {
                backgroundColor: colors.silverGray,
                boxShadow: "none",
            },
        },
        variant === "secondary" && {
            color: colors.red,
            backgroundColor: "transparent",
            borderColor: "currentcolor",
            borderStyle: "solid",
            ":hover:not(:disabled)": {
                // brightRed at 0.06 alpha
                backgroundColor: "rgba(255, 20, 35, 0.06)",
            },
            ":disabled": {
                color: colors.silverGray,
            },
        },
        variant === "text" && {
            backgroundColor: "transparent",
            borderStyle: "none",
            color: colors.darkGray2,
            ":hover:not(:disabled)": {
                // darkGray1 at 6% alpha
                backgroundColor: "rgba(64, 70, 76, 0.06)",
            },
            ":disabled": {
                color: colors.silverGray,
            },
        },
        variant === "bordered" && {
            backgroundColor: colors.white,
            color: colors.silverGrayDark,
            border: `1px solid ${colors.silverGrayLight}`,
            ":hover:not(:disabled)": {
                backgroundColor: colors.lightGray1,
                color: colors.darkGray2,
            },
            ":disabled": {
                backgroundColor: colors.lightGray2,
                border: `1px solid ${colors.lightGray2}`,
                color: colors.silverGrayDark,
            },
        },
        variant === "tertiary" && {
            color: colors.darkGray2,
            border: "none",
            backgroundColor: colors.lightGray2,
            ":hover:not(:disabled)": {
                backgroundColor: colors.white,
            },
            ":disabled": {
                backgroundColor: colors.lightGray2,
                color: colors.silverGrayLight,
            },
        },
        // edge
        edge === "rounded" && {
            borderRadius: "4px",
        },
        edge === "circular" && {
            // Pill-shaped buttons... Overlapping border radius get clipped to pill.
            borderRadius: "100vh",
        },
        // color
        color === "secondary" && {
            color: theme.custom.colors.silverGray,
            borderColor: theme.custom.colors.silverGray,
            ":hover:not(:disabled)": {
                backgroundColor: theme.custom.colors.lightGray1,
            },
        },
    ]);
};
const ButtonRoot = styled("button", {
    shouldForwardProp: shouldForwardButtonProp,
})(buttonStyles);
const ButtonLinkRoot = styled(LinkAdapter, {
    shouldForwardProp: shouldForwardButtonProp,
})(buttonStyles);
const IconContainer = styled.span(({ size, side }) => [
    {
        height: "1em",
        display: "flex",
        alignItems: "center",
    },
    side === "start" && {
        /**
         * The negative margin is to counteract the padding on the button itself.
         * Without icons, the left space is 24/16/12 px.
         * With icons, the left space is 20/12/8 px.
         */
        marginLeft: "-4px",
        marginRight: "8px",
    },
    side === "end" && {
        marginLeft: "8px",
        marginRight: "-4px",
    },
    {
        "& svg, & .MuiSvgIcon-root": {
            width: "1em",
            height: "1em",
            fontSize: pxToRem({
                small: 16,
                medium: 20,
                large: 24,
            }[size]),
        },
    },
]);
const ButtonInner = (props) => {
    const { children, size = DEFAULT_PROPS.size } = props;
    return (React.createElement(React.Fragment, null,
        props.startIcon ? (React.createElement(IconContainer, { size: size, side: "start" }, props.startIcon)) : null,
        children,
        props.endIcon ? (React.createElement(IconContainer, { size: size, side: "end" }, props.endIcon)) : null));
};
/**
 * Our standard button component. See [Button Docs](https://mitodl.github.io/smoot-design/?path=/docs/smoot-design-button--docs).
 *
 * See also:
 * - [ButtonLink](https://mitodl.github.io/smoot-design/?path=/docs/smoot-design-button--docs#links)
 * - [ActionButton](https://mitodl.github.io/smoot-design/?path=/docs/smoot-design-actionbutton--docs) for icon-only uses
 */
const Button = React.forwardRef((_a, ref) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    return (React.createElement(ButtonRoot, Object.assign({ ref: ref, type: "button" }, props),
        React.createElement(ButtonInner, Object.assign({}, props), children)));
});
Button.displayName = "Button";
/**
 * See [ButtonLink docs](https://mitodl.github.io/smoot-design/?path=/docs/smoot-design-button--docs#links)
 */
const ButtonLink = React.forwardRef((_a, ref) => {
    var { children, Component } = _a, props = __rest(_a, ["children", "Component"]);
    return (React.createElement(ButtonLinkRoot, Object.assign({ Component: Component, ref: ref }, props),
        React.createElement(ButtonInner, Object.assign({}, props), children)));
});
ButtonLink.displayName = "ButtonLink";
export { Button, ButtonLink, ButtonRoot, DEFAULT_PROPS, ButtonLinkRoot, RESPONSIVE_SIZES, };
